// src/pages/CollectionPage.tsx
import React, { useMemo, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { COLLECTION_QUERY } from "../graphql/collections";
import { buildImageUrl } from "../config";
import {
  Box,
  Chip,
  Container,
  Grid,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Link,
  Dialog,
  DialogContent,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_PHONE;

function buildWhatsappLink(opts: { number?: string; message: string }) {
  const text = encodeURIComponent(opts.message);
  return opts.number
    ? `https://wa.me/${opts.number}?text=${text}`
    : `https://wa.me/?text=${text}`;
}

export default function CollectionPage() {
  const { slug } = useParams();
  const { data } = useQuery(COLLECTION_QUERY, { variables: { slug } });

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));

  const [preview, setPreview] = useState<{ src: string; index: number } | null>(
    null,
  );

  const c = data?.collection;

  const images = c?.images ?? [];
  const heroImage = images?.[0] ? buildImageUrl(images[0]) : null;

  const sortedImages: string[] = useMemo(() => {
    const arr = [...images];
    return arr;
  }, [images]);

  if (!c) return null;

  const title = c.name ?? "Collection";

  return (
    <>
      <Box sx={{ bgcolor: "#fff" }}>
        <Box
          sx={{ position: "relative", overflow: "hidden", bgcolor: "#0b0b0b" }}
        >
          {heroImage ? (
            <Box
              component="img"
              loading="lazy"
              decoding="async"
              src={heroImage}
              alt={title}
              sx={{
                width: "100%",
                height: { xs: 420, sm: 423, md: 520 },
                objectFit: "cover",
                display: "block",
                transform: "scale(1.03)",
                filter: "contrast(1.05) saturate(1.03)",
              }}
            />
          ) : (
            <Box sx={{ height: { xs: 420, sm: 423, md: 520 } }} />
          )}

          {/* Gradients */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(90deg, rgba(0,0,0,.78) 0%, rgba(0,0,0,.40) 52%, rgba(0,0,0,.08) 100%)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(0,0,0,.35) 0%, rgba(0,0,0,.12) 40%, rgba(0,0,0,.72) 100%)",
            }}
          />

          <Container
            maxWidth="lg"
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "flex-end",
              pb: { xs: 4, sm: 5, md: 6 },
            }}
          >
            <Box sx={{ width: "100%" }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1.5 }}
              >
                <Link
                  component={RouterLink}
                  to="/"
                  underline="hover"
                  sx={{ color: "rgba(255,255,255,.78)", fontSize: 13 }}
                >
                  Home
                </Link>
                <Typography
                  sx={{ color: "rgba(255,255,255,.55)", fontSize: 13 }}
                >
                  /
                </Typography>
                <Typography
                  sx={{ color: "rgba(255,255,255,.92)", fontSize: 13 }}
                >
                  {title}
                </Typography>
              </Stack>

              <Typography
                sx={{
                  fontFamily: `"Bodoni Moda", serif`,
                  fontSize: { xs: "2.1rem", sm: "2.9rem", md: "3.7rem" },
                  lineHeight: 1.02,
                  letterSpacing: -0.5,
                  color: "white",
                  textShadow: "0 18px 40px rgba(0,0,0,.35)",

                  textTransform: "uppercase",
                }}
              >
                {title}
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* =========================
            GRID
           ========================= */}
        <Container maxWidth="lg" sx={{ pt: 4, pb: 9 }}>
          <Box id="collection-grid" sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontFamily: `"Bodoni Moda", serif`,
                fontSize: { xs: "1.7rem", sm: "2.1rem" },
                lineHeight: 1.1,
                mb: 0.6,
                textAlign: "center",
                fontWeight: 700,
              }}
            >
              Explore the Collection
            </Typography>
            <Typography
              sx={{
                color: "text.secondary",
                lineHeight: 1.8,
                textAlign: "center",
                fontSize: "small",
                paddingTop: "11px",
                paddingBottom: "11px",

                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {c?.description || ""}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Chip
                label={`${sortedImages.length} PIECES`}
                sx={{
                  borderRadius: 999,
                  bgcolor: "rgba(255,255,255,.12)",
                  color: "black",
                  border: "1px solid black",
                  backdropFilter: "blur(10px)",
                  fontWeight: 700,
                  letterSpacing: 0.6,
                }}
              />
            </Stack>
          </Box>

          <Grid container spacing={{ xs: 1.4, sm: 2, md: 3 }}>
            {sortedImages.map((img: string, idx: number) => {
              const imgUrl = buildImageUrl(img);

              const isFeatured = idx === 0 && isMdUp;

              const message = [
                `Hi! I want to inquire about this item from "${title}".`,
                "",
                `Image: ${imgUrl}`,
                slug
                  ? `Collection: ${window.location.origin}/collections/${slug}`
                  : "",
                "",
                "Please share price and availability.",
              ]
                .filter(Boolean)
                .join("\n");

              const waLink = buildWhatsappLink({
                number: WHATSAPP_NUMBER,
                message,
              });

              return (
                <Grid
                  key={`${img}-${idx}`}
                  item
                  xs={6}
                  sm={6}
                  md={isFeatured ? 8 : 4}
                >
                  <Box
                    sx={{
                      position: "relative",
                      borderRadius: { xs: 2.2, sm: 3 },
                      overflow: "hidden",
                      bgcolor: "#f4f4f4",
                      border: "1px solid rgba(0,0,0,.08)",
                      boxShadow: "0 18px 45px rgba(0,0,0,.08)",
                      transform: "translateZ(0)",
                      "&:hover .img": { transform: "scale(1.05)" },
                      "&:hover .veil": { opacity: 1 },
                      "&:hover .actions": {
                        opacity: 1,
                        transform: "translateY(0)",
                      },
                    }}
                  >
                    <Box
                      onClick={() => setPreview({ src: imgUrl, index: idx })}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setPreview({ src: imgUrl, index: idx });
                        }
                      }}
                      sx={{
                        cursor: "zoom-in",
                        aspectRatio: isFeatured
                          ? isSmUp
                            ? "20.8 / 10"
                            : "1 / 1"
                          : "1 / 1",
                      }}
                    >
                      <Box
                        component="img"
                        loading="lazy"
                        decoding="async"
                        src={imgUrl}
                        alt={`${title} ${idx + 1}`}
                        className="img"
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                          transition: "transform .6s ease",
                        }}
                      />
                    </Box>

                    {/* soft veil */}
                    <Box
                      className="veil"
                      sx={{
                        position: "absolute",
                        inset: 0,
                        opacity: 0,
                        transition: "opacity .25s ease",
                        background:
                          "linear-gradient(180deg, rgba(0,0,0,.00) 45%, rgba(0,0,0,.55) 100%)",
                        pointerEvents: "none",
                      }}
                    />

                    {/* WhatsApp icon (small on mobile, not a big button) */}
                    <Tooltip title="WhatsApp inquiry" arrow>
                      <IconButton
                        component="a"
                        href={waLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          position: "absolute",
                          right: { xs: 8, sm: 10 },
                          bottom: { xs: 8, sm: 10 },
                          width: { xs: 34, sm: 38 },
                          height: { xs: 34, sm: 38 },
                          bgcolor: "rgba(255,255,255,.92)",
                          border: "1px solid rgba(0,0,0,.10)",
                          boxShadow: "0 10px 25px rgba(0,0,0,.12)",
                          "&:hover": { bgcolor: "white" },
                        }}
                        aria-label="WhatsApp inquiry"
                      >
                        <WhatsAppIcon
                          sx={{
                            fontSize: { xs: 18, sm: 20 },
                            color: "#25D366",
                          }}
                        />
                      </IconButton>
                    </Tooltip>

                    {isMdUp ? (
                      <Box
                        className="actions"
                        sx={{
                          position: "absolute",
                          left: 12,
                          right: 12,
                          bottom: 12,
                          opacity: 0,
                          transform: "translateY(10px)",
                          transition: "all .25s ease",
                          display: "flex",
                          justifyContent: "flex-start",
                          pointerEvents: "none",
                        }}
                      >
                        <Chip
                          label="Preview"
                          size="small"
                          sx={{
                            borderRadius: 999,
                            bgcolor: "rgba(255,255,255,.18)",
                            color: "white",
                            border: "1px solid rgba(255,255,255,.22)",
                            backdropFilter: "blur(10px)",
                          }}
                        />
                      </Box>
                    ) : null}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* =========================
          IMAGE PREVIEW MODAL
         ========================= */}
      <Dialog
        open={!!preview}
        onClose={() => setPreview(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "rgba(10,10,10,.92)",
            backdropFilter: "blur(10px)",
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 30px 90px rgba(0,0,0,.55)",
          },
        }}
      >
        <DialogContent sx={{ p: 0, position: "relative" }}>
          {/* Close */}
          <IconButton
            onClick={() => setPreview(null)}
            aria-label="Close preview"
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 2,
              bgcolor: "rgba(255,255,255,.10)",
              border: "1px solid rgba(255,255,255,.18)",
              color: "white",
              "&:hover": { bgcolor: "rgba(255,255,255,.16)" },
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Image */}
          {preview?.src ? (
            <Box
              component="img"
              loading="lazy"
              decoding="async"
              src={preview.src}
              alt={`${title} preview`}
              sx={{
                width: "100%",
                maxHeight: "82vh",
                objectFit: "contain",
                display: "block",
                bgcolor: "rgba(0,0,0,.55)",
              }}
            />
          ) : null}

          {/* Bottom actions (compact) */}
          <Box
            sx={{
              p: { xs: 1.4, sm: 2 },
              display: "flex",
              gap: 1,
              alignItems: "center",
              justifyContent: "space-between",
              borderTop: "1px solid rgba(255,255,255,.10)",
              bgcolor: "rgba(0,0,0,.40)",
            }}
          >
            <Box>
              <Typography
                sx={{
                  color: "rgba(255,255,255,.92)",
                  fontFamily: `"Bodoni Moda", serif`,
                  fontSize: { xs: "1.1rem", sm: "1.25rem" },
                  lineHeight: 1.2,
                }}
              >
                {title}
              </Typography>
              <Typography
                sx={{ color: "rgba(255,255,255,.65)", fontSize: 12, mt: 0.4 }}
              >
                Piece #{(preview?.index ?? 0) + 1}
              </Typography>
            </Box>

            <IconButton
              aria-label="WhatsApp inquiry"
              onClick={() => {
                if (!preview?.src) return;

                const message = [
                  `Hi! I want to inquire about this item from "${title}".`,
                  "",
                  `Image: ${preview.src}`,
                  slug
                    ? `Collection: ${window.location.origin}/collections/${slug}`
                    : "",
                  "",
                  "Please share price and availability.",
                ]
                  .filter(Boolean)
                  .join("\n");

                const waLink = buildWhatsappLink({
                  number: WHATSAPP_NUMBER,
                  message,
                });

                window.open(waLink, "_blank", "noopener,noreferrer");
              }}
              sx={{
                width: 44,
                height: 44,
                bgcolor: "rgba(255,255,255,.10)",
                border: "1px solid rgba(255,255,255,.18)",
                color: "white",
                "&:hover": { bgcolor: "rgba(255,255,255,.16)" },
              }}
            >
              <WhatsAppIcon sx={{ color: "#25D366" }} />
            </IconButton>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
